// Autobahn (auto.de redesign) — Kontakt & Rückruf (/account/kontakt), ADV-270.
//
// Figma "KONTO | 12 Kontakt" (desktop 64210:9826 / mobile 64210:9912, content frames 64223:13970 /
// 64223:14075): breadcrumb back to the dashboard, "Nachricht senden" on the left, "Rückruf
// vereinbaren" and "Direkt erreichbar" on the right (stacked on mobile).
// Both actions post the same account contact request as before (carStore.submitContactForm with
// the category the backend routes on): the message with Anliegen / Referenz written in front of
// the text, the callback with the chosen window's start as callBackRequest (unix seconds, as the
// old TimePicker sent it). E-mail, phone and message stay on the shared ContactPageFormConfig (its
// day/time fields belong to the TimePicker of the checkout and offer modals). Telefon follows the
// profile once it has loaded: with a saved phone one field as in Figma (the whole number, "+"
// first, digits only), without one Land / Vorwahl / Telefonnummer as before (digits only).
// Page-local styles: ./ContactPage.css.
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import * as React from 'react';
import classNames from 'classnames';
import { translate } from 'react-i18next';
import { observer, inject } from 'mobx-react';
import { Link } from 'react-router-dom';
import form from './ContactPageFormConfig';
import { getCallbackSlots, formatSlotTime } from './callbackSlots';
import { toWholeNumber, toDigits } from './phoneInput';
import { supportPhone, supportPhoneLink } from '../../../framework/constants/app';
import { PHONE_PREFIXES, PHONE_PREFIX_DEFAULT } from '../../../framework/constants/forms';
import CookieUtils from 'framework/utils/CookieUtils';
import { getDeviceType } from '../../../framework/utils/CommonUtils';
import Page from '../../containers/Page';
import { DEFAULT_EMPTY_VALUE } from '../../../framework/constants/profile';
import { Icon, FormField, Dropdown } from '@bytebrand/fe-ui-core-autobahn/ui';
import breadcrumbChevron from './icons/breadcrumb-chevron.svg';
import breadcrumbChevronSm from './icons/breadcrumb-chevron-sm.svg';
import phoneIcon from './icons/phone.svg';
import './ContactPage.css';
/** user-actions-contact REQUEST_CATEGORY.USER_CONTACT: the backend keeps phone and callback only for it */
const USER_CONTACT_CATEGORY = 'Kontaktanfrage aus Useraccount';
const SUBJECTS = ['order', 'vehicle', 'financing', 'delivery', 'account', 'other'];
/** phoneMain's rules and part defaults per Telefon layout ('split' = the form's own set) */
const PHONE_SETUP = {
    single: { rules: { countryCode: '', areaCode: '', phoneNumber: 'required|phone' }, countryCode: '' },
    split: {
        rules: { countryCode: 'required', areaCode: 'required|phone_areaCode', phoneNumber: 'required|phone_phoneNumber' },
        countryCode: PHONE_PREFIX_DEFAULT.value,
    },
};
const PHONE_PATHS = { single: ['phoneMain.phoneNumber'], split: ['phoneMain.areaCode', 'phoneMain.phoneNumber'] };
const PREFIX_OPTIONS = PHONE_PREFIXES.map((prefix) => ({ value: prefix.value, label: prefix.label }));
const SENT_MS = 10000;
const SLOT_KEY_STEPS = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
/** where focus goes when a form path fails validation */
const FIELD_TARGETS = {
    email: '#ct-email',
    'phoneMain.areaCode': '#ct-area-code',
    'phoneMain.phoneNumber': '#ct-phone',
    message: '#ct-message',
};
let ContactNextPage = class ContactNextPage extends React.Component {
    constructor(props) {
        super(props);
        Object.defineProperty(this, "inputRef", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: React.createRef()
        });
        Object.defineProperty(this, "slotRefs", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "sentTimer", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        /** state.phoneMode for the value interceptors, which run before a setState lands */
        Object.defineProperty(this, "phoneMode", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 'loading'
        });
        /** Telefon as the profile has it: one field with the saved phone, else the empty Land / Vorwahl / Telefonnummer */
        Object.defineProperty(this, "applyPhoneMode", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                const { profileStore: { profilePhone } } = this.props;
                const phoneMode = profilePhone !== DEFAULT_EMPTY_VALUE ? 'single' : 'split';
                const setup = PHONE_SETUP[phoneMode];
                this.phoneMode = phoneMode;
                form.$('phoneMain').set('rules', setup.rules);
                form.$('phoneMain.countryCode').set('default', setup.countryCode);
                form.$('phoneMain.areaCode').set('default', '');
                form.$('phoneMain.phoneNumber').set('default', phoneMode === 'single' ? profilePhone : '');
                form.$('phoneMain').reset();
                form.$('phoneMain').showErrors(false);
                this.setState({ phoneMode });
            }
        });
        Object.defineProperty(this, "contactPaths", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => ['email', ...PHONE_PATHS[this.phoneMode === 'split' ? 'split' : 'single']]
        });
        /** the honeypot input is filled by bots only */
        Object.defineProperty(this, "isBot", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => !!this.inputRef.current && this.inputRef.current.value.length > 0
        });
        /** validates the fields with their errors shown and focuses the first invalid one; true when all pass */
        Object.defineProperty(this, "validateFields", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async (paths) => {
                await Promise.all(paths.map((path) => form.$(path).validate({ showErrors: true })));
                const invalid = paths.find((path) => form.$(path).hasError);
                if (!invalid)
                    return true;
                const target = document.querySelector(FIELD_TARGETS[invalid]);
                if (target)
                    target.focus();
                return false;
            }
        });
        Object.defineProperty(this, "postContact", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (message, callBackRequest) => {
                const { carStore, lng, authStore: { userId } } = this.props;
                const { email, phoneMain } = form.values();
                const contactFormData = {
                    cookieId: CookieUtils.resolveCookieId() || null, carId: null, email, phoneNumber: phoneMain, message,
                    category: USER_CONTACT_CATEGORY, device: getDeviceType(), lang: lng, requestUserId: userId,
                };
                if (callBackRequest)
                    contactFormData.callBackRequest = callBackRequest;
                if (userId)
                    contactFormData.userId = userId;
                return carStore.submitContactForm(contactFormData);
            }
        });
        Object.defineProperty(this, "showSent", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (sent, sentSlot = '') => {
                if (this.sentTimer)
                    clearTimeout(this.sentTimer);
                this.setState({ sent, sentSlot });
                this.sentTimer = setTimeout(() => this.setState({ sent: null }), SENT_MS);
            }
        });
        Object.defineProperty(this, "logFailure", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (err) => {
                // Failed POST: submitForm already reset contactFormIsFetching (the buttons re-enable) and set
                // formStatus='error'. Handled here so it is not an unhandled rejection and no success shows.
                console.error('ContactNextPage: submitContactForm failed', err);
            }
        });
        Object.defineProperty(this, "handleMessageSubmit", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async () => {
                if (this.isBot())
                    return;
                if (!(await this.validateFields([...this.contactPaths(), 'message'])))
                    return;
                const { t } = this.props;
                const { subject, reference } = this.state;
                const head = [`${t('messageCard.subject')}: ${t(`subjects.${subject}`)}`];
                if (reference.trim())
                    head.push(`${t('messageCard.reference')}: ${reference.trim()}`);
                this.postContact(`${head.join('\n')}\n\n${form.$('message').value}`).then(() => {
                    this.showSent('message');
                    this.setState({ reference: '' });
                    form.$('message').reset();
                    form.$('message').showErrors(false);
                }).catch(this.logFailure);
            }
        });
        Object.defineProperty(this, "handleCallbackSubmit", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async (slot) => {
                if (this.isBot())
                    return;
                if (!(await this.validateFields(this.contactPaths())))
                    return;
                const { t } = this.props;
                const label = this.slotLabel(slot);
                this.postContact(t('callbackCard.supportMessage', { slot: label }), Math.round(slot.start.getTime() / 1000))
                    .then(() => this.showSent('callback', label))
                    .catch(this.logFailure);
            }
        });
        Object.defineProperty(this, "slotLabel", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (slot) => {
                const { t, lng } = this.props;
                const day = slot.day === 'today' ? t('callbackCard.today')
                    : slot.day === 'tomorrow' ? t('callbackCard.tomorrow')
                        : slot.start.toLocaleDateString(lng === 'en' ? 'en-GB' : 'de-DE', { weekday: 'long' });
                return `${day}, ${formatSlotTime(slot)}`;
            }
        });
        /** radio group keys: arrows move the choice (and the focus) through the windows */
        Object.defineProperty(this, "onSlotKeyDown", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (event, slots, index) => {
                const step = SLOT_KEY_STEPS[event.key];
                if (!step)
                    return;
                event.preventDefault();
                const next = (index + step + slots.length) % slots.length;
                this.setState({ slotStart: slots[next].start.getTime() });
                const el = this.slotRefs[next];
                if (el)
                    el.focus();
            }
        });
        /** the number the callback card names: the phone field(s) as they stand */
        Object.defineProperty(this, "callbackPhone", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                const number = String(form.$('phoneMain.phoneNumber').value || '');
                if (this.state.phoneMode !== 'split')
                    return number === '+' ? '' : number;
                const area = String(form.$('phoneMain.areaCode').value || '');
                if (!area && !number)
                    return '';
                return [String(form.$('phoneMain.countryCode').value || ''), area, number].filter(Boolean).join(' ');
            }
        });
        this.state = { phoneMode: 'loading', subject: SUBJECTS[0], reference: '', slotStart: null, sent: null, sentSlot: '' };
    }
    componentDidMount() {
        // what may be typed: "+" and digits in the one field, digits in Vorwahl / Telefonnummer
        form.$('phoneMain.phoneNumber').intercept({
            key: 'value',
            call: ({ change }) => {
                if (this.phoneMode === 'single')
                    change.newValue = toWholeNumber(change.newValue);
                if (this.phoneMode === 'split')
                    change.newValue = toDigits(change.newValue, 12);
                return change;
            },
        });
        form.$('phoneMain.areaCode').intercept({
            key: 'value',
            call: ({ change }) => { change.newValue = toDigits(change.newValue, 5); return change; },
        });
        const { profileStore } = this.props;
        // a profile loaded before (dashboard, menu) decides the layout right away
        if (profileStore.isProfileLoaded)
            this.applyPhoneMode();
        profileStore.getFullUserProfile().then(({ profile: { email } }) => {
            form.set('default', { email });
            form.each((field) => {
                if (!field.hasNestedFields && field.name !== 'callBackRequest') {
                    field.reset(false);
                    field.showErrors(false);
                }
            });
            this.applyPhoneMode();
        }).catch(this.applyPhoneMode);
    }
    componentWillUnmount() {
        if (this.sentTimer)
            clearTimeout(this.sentTimer);
        // the form outlives the page
        ['phoneMain.phoneNumber', 'phoneMain.areaCode'].forEach((path) => form.$(path).dispose({ type: 'interceptor', key: 'value' }));
    }
    renderSent(kind) {
        const { t } = this.props;
        const { sent, sentSlot } = this.state;
        if (sent !== kind)
            return null;
        return (React.createElement("p", { className: "ct-sent", role: "status" },
            React.createElement(Icon, { name: "check-circle", size: 20 }),
            kind === 'message' ? t('messageCard.sent') : t('callbackCard.sent', { slot: sentSlot })));
    }
    renderPhoneField() {
        const { t } = this.props;
        const { phoneMode } = this.state;
        const phoneNumber = form.$('phoneMain.phoneNumber');
        if (phoneMode !== 'split') {
            // locked while loading: which layout (and which rule) applies is not known yet
            return (React.createElement("div", { className: classNames('ct-field', { 'is-empty': !phoneNumber.value }) },
                React.createElement("label", { className: "ct-label", htmlFor: "ct-phone" }, t('messageCard.phone')),
                React.createElement(FormField, { id: "ct-phone", field: phoneNumber, t: t, hideLabel: true, autoComplete: "tel", disabled: phoneMode === 'loading' })));
        }
        const countryCode = form.$('phoneMain.countryCode');
        const areaCode = form.$('phoneMain.areaCode');
        return (React.createElement("div", { className: "ct-phone-split" },
            React.createElement("div", { className: "ct-field ct-field--select" },
                React.createElement("span", { className: "ct-label", "aria-hidden": "true" }, t('messageCard.countryCode')),
                React.createElement("div", { className: "mfield-group" },
                    React.createElement(Dropdown, { value: String(countryCode.value || ''), options: PREFIX_OPTIONS, ariaLabel: t('messageCard.countryCode'), onChange: (value) => countryCode.onChange(value) }))),
            React.createElement("div", { className: classNames('ct-field ct-field--outline-error', { 'is-empty': !areaCode.value }) },
                React.createElement("label", { className: "ct-label", htmlFor: "ct-area-code" }, t('messageCard.areaCode')),
                React.createElement(FormField, { id: "ct-area-code", field: areaCode, t: t, hideLabel: true, autoComplete: "tel-area-code" })),
            React.createElement("div", { className: classNames('ct-field', { 'is-empty': !phoneNumber.value }) },
                React.createElement("label", { className: "ct-label", htmlFor: "ct-phone" }, t('messageCard.phoneNumber')),
                React.createElement(FormField, { id: "ct-phone", field: phoneNumber, t: t, hideLabel: true, autoComplete: "tel-local" }))));
    }
    renderMessageCard() {
        const { t, carStore: { contactFormIsFetching } } = this.props;
        const { subject, reference } = this.state;
        const msg = form.$('message');
        const msgError = msg.hasError && msg.error ? msg.error : '';
        const subjectOpts = SUBJECTS.map((key) => ({ value: key, label: t(`subjects.${key}`) }));
        return (React.createElement("section", { className: "ct-panel ct-panel--message", "aria-labelledby": "ct-message-title" },
            React.createElement("div", { className: "ct-head" },
                React.createElement("h2", { id: "ct-message-title", className: "ct-head-title" }, t('messageCard.title'))),
            React.createElement("div", { className: "ct-body" },
                React.createElement("div", { className: "ct-form" },
                    React.createElement("div", { className: "ct-row" },
                        React.createElement("div", { className: "ct-field ct-field--select" },
                            React.createElement("span", { className: "ct-label", "aria-hidden": "true" }, t('messageCard.subject')),
                            React.createElement("div", { className: "mfield-group" },
                                React.createElement(Dropdown, { value: subject, options: subjectOpts, ariaLabel: t('messageCard.subject'), onChange: (value) => this.setState({ subject: value }) }))),
                        React.createElement("div", { className: classNames('ct-field', { 'is-empty': !reference }) },
                            React.createElement("label", { className: "ct-label", htmlFor: "ct-reference" }, t('messageCard.reference')),
                            React.createElement("div", { className: "mfield-group" },
                                React.createElement("div", { className: "mfield" },
                                    React.createElement("input", { id: "ct-reference", name: "reference", type: "text", autoComplete: "off", spellCheck: false, value: reference, maxLength: 100, onChange: (e) => this.setState({ reference: e.target.value }) }))))),
                    React.createElement("div", { className: "ct-row" },
                        React.createElement("div", { className: "ct-field" },
                            React.createElement("label", { className: "ct-label", htmlFor: "ct-email" }, t('messageCard.email')),
                            React.createElement(FormField, { id: "ct-email", field: form.$('email'), t: t, hideLabel: true, disabled: true })),
                        this.renderPhoneField()),
                    React.createElement("div", { className: "ct-field ct-field--area" },
                        React.createElement("label", { className: "ct-label", htmlFor: "ct-message" }, t('messageCard.message')),
                        React.createElement("div", { className: "mfield-group" },
                            React.createElement("div", { className: classNames('mfield ct-area', { 'mfield--error': !!msgError }) },
                                React.createElement("textarea", { id: "ct-message", name: "message", value: msg.value, "aria-invalid": !!msgError, "aria-describedby": msg.value ? undefined : 'ct-message-hint', onChange: (e) => msg.onChange(e.target.value, msg.name) }),
                                !msg.value ? React.createElement("span", { id: "ct-message-hint", className: "ct-area-hint" }, t('messageCard.messageHint')) : null),
                            msgError ? React.createElement("span", { className: "mfield-error-text" }, msgError) : null))),
                React.createElement("input", { ref: this.inputRef, type: "text", className: "ct-honeypot", tabIndex: -1, autoComplete: "off", "aria-hidden": "true" }),
                React.createElement("div", { className: "ct-actions ct-actions--end" },
                    React.createElement("button", { type: "button", className: "ct-btn ct-btn--filled", disabled: contactFormIsFetching, "aria-busy": contactFormIsFetching, onClick: this.handleMessageSubmit }, t('messageCard.send'))),
                this.renderSent('message'))));
    }
    renderCallbackCard() {
        const { t, carStore: { contactFormIsFetching } } = this.props;
        const slots = getCallbackSlots(new Date());
        const selected = slots.find((slot) => slot.start.getTime() === this.state.slotStart) || slots[0];
        const phone = this.callbackPhone();
        return (React.createElement("section", { className: "ct-panel", "aria-labelledby": "ct-callback-title" },
            React.createElement("div", { className: "ct-head" },
                React.createElement("h2", { id: "ct-callback-title", className: "ct-head-title" }, t('callbackCard.title'))),
            React.createElement("div", { className: "ct-body" },
                React.createElement("p", { className: "ct-callback-text" }, phone ? (React.createElement(React.Fragment, null,
                    t('callbackCard.textBefore'),
                    React.createElement("strong", null, phone),
                    t('callbackCard.textAfter'))) : t('callbackCard.textNoPhone')),
                React.createElement("div", { className: "ct-slots", role: "radiogroup", "aria-label": t('callbackCard.slotsLabel') }, slots.map((slot, index) => {
                    const active = slot === selected;
                    return (React.createElement("button", { key: slot.start.getTime(), ref: (el) => { this.slotRefs[index] = el; }, type: "button", role: "radio", "aria-checked": active, tabIndex: active ? 0 : -1, className: classNames('ct-chip', { 'is-active': active }), onClick: () => this.setState({ slotStart: slot.start.getTime() }), onKeyDown: (e) => this.onSlotKeyDown(e, slots, index) }, this.slotLabel(slot)));
                })),
                React.createElement("div", { className: "ct-actions" },
                    React.createElement("button", { type: "button", className: "ct-btn ct-btn--outlined ct-btn--block", disabled: contactFormIsFetching, "aria-busy": contactFormIsFetching, onClick: () => this.handleCallbackSubmit(selected) }, t('callbackCard.send'))),
                this.renderSent('callback'))));
    }
    renderDirectCard() {
        const { t } = this.props;
        return (React.createElement("section", { className: "ct-panel", "aria-labelledby": "ct-direct-title" },
            React.createElement("div", { className: "ct-head" },
                React.createElement("h2", { id: "ct-direct-title", className: "ct-head-title" }, t('directCard.title'))),
            React.createElement("div", { className: "ct-body" },
                React.createElement("div", { className: "ct-direct" },
                    React.createElement("span", { className: "ct-direct-ic", "aria-hidden": "true" },
                        React.createElement("span", { className: "ct-direct-glyph" },
                            React.createElement("img", { src: phoneIcon, alt: "", width: 18, height: 18 }))),
                    React.createElement("div", { className: "ct-direct-text" },
                        React.createElement("a", { className: "ct-direct-number", href: `tel:${supportPhoneLink}` }, supportPhone),
                        React.createElement("span", { className: "ct-direct-hours" }, t('directCard.hours')))))));
    }
    render() {
        const { t } = this.props;
        return (React.createElement(Page, { page: "KONTAKT_SETTINGS", mainContentClass: "ct-main" },
            React.createElement("div", { className: "md3" },
                React.createElement("div", { className: "ct-page" },
                    React.createElement("nav", { className: "ct-crumbs", "aria-label": t('crumbs.label') },
                        React.createElement(Link, { className: "ct-crumb-link", to: "/account/dashboard" }, t('crumbs.dashboard')),
                        React.createElement("img", { className: "ct-crumb-sep", src: breadcrumbChevron, alt: "", width: 14, height: 14 }),
                        React.createElement("img", { className: "ct-crumb-sep ct-crumb-sep--sm", src: breadcrumbChevronSm, alt: "", width: 13, height: 13 }),
                        React.createElement("h1", { className: "ct-crumb-current", "aria-current": "page" }, t('crumbs.title'))),
                    React.createElement("div", { className: "ct-grid" },
                        this.renderMessageCard(),
                        React.createElement("div", { className: "ct-side" },
                            this.renderCallbackCard(),
                            this.renderDirectCard()))))));
    }
};
ContactNextPage = __decorate([
    inject('carStore', 'profileStore', 'authStore'),
    observer,
    __metadata("design:paramtypes", [Object])
], ContactNextPage);
export default translate(['ContactPage'])(ContactNextPage);
